import { SelectFieldSync, SelectFieldSyncProps } from '@servicetitan/anvil2/beta';
import { Controller, get, useFormContext } from 'react-hook-form';

export interface SelectFieldOption {
    id: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectFieldProps extends Omit<
    SelectFieldSyncProps,
    'onSelectedOptionChange' | 'value'
> {
    name: string;
    onChange?: (value: SelectFieldOption | null) => void;
}

export const SelectField = ({ name, onChange, options, ...props }: SelectFieldProps) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = get(errors, name);
    let errorMessage = error?.message;

    // Zod has trouble with null values, so we need to handle them
    if (error && errorMessage === 'Expected string, received null') {
        const fieldName = name.split('.').pop() ?? name;

        const readableFieldName = fieldName
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .trim();

        // Use "an" for vowels, "a" for consonants
        const article = /^[aeiou]/.test(readableFieldName) ? 'an' : 'a';
        errorMessage = `Please select ${article} ${readableFieldName}`;
    }

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                // Find the selected option based on the stored value (id)
                const selectedOption = options.find(option => option.id === field.value) ?? null;

                return (
                    <SelectFieldSync
                        {...props}
                        options={options}
                        value={selectedOption}
                        onSelectedOptionChange={option => {
                            // Store only the id in form state
                            field.onChange(option?.id ?? null);
                            onChange?.(option);
                        }}
                        error={errorMessage}
                    />
                );
            }}
        />
    );
};
